import { ChevronDown, Loader2, Lock } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import {
  FaBan,
  FaBullhorn,
  FaCheckCircle,
  FaCode,
  FaHandshake,
  FaHeadset,
  FaUser,
  FaUserShield,
  FaUserTie,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { RolePermissionsMatrix } from "src/components/RolePermissionsMatrix";
import { actorFromAuth } from "src/lib/activityService";
import {
  ALL_USER_ROLES,
  getRoleDisplayName,
  hasPermission,
  Permission,
  UserRole,
  type UserProfile,
} from "src/lib/roles";
import {
  getAllUsers,
  updateUserActiveStatus,
  updateUserRole,
} from "src/lib/userService";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import { SettingsHeader } from "./SettingsThemesSection";

const nunito = { fontFamily: "'Nunito', system-ui, sans-serif" } as const;

interface UserWithUid extends UserProfile {
  uid: string;
}

function getRoleIcon(role: UserRole) {
  switch (role) {
    case UserRole.ADMIN:
      return <FaUserShield className="text-red-400" />;
    case UserRole.DEVELOPER:
      return <FaCode className="text-emerald-400" />;
    case UserRole.PRODUCT:
      return <FaUserTie className="text-blue-400" />;
    case UserRole.MARKETING:
      return <FaBullhorn className="text-pink-400" />;
    case UserRole.SALES:
      return <FaHandshake className="text-amber-400" />;
    case UserRole.CUSTOMER_SUPPORT:
      return <FaHeadset className="text-cyan-400" />;
    case UserRole.VIEWER:
      return <FaUser className="text-gray-400" />;
  }
}

function getRoleBadgeColor(role: UserRole) {
  switch (role) {
    case UserRole.ADMIN:
      return "bg-red-500/20 text-red-300 border-red-500/50";
    case UserRole.DEVELOPER:
      return "bg-emerald-500/20 text-emerald-300 border-emerald-500/50";
    case UserRole.PRODUCT:
      return "bg-blue-500/20 text-blue-300 border-blue-500/50";
    case UserRole.MARKETING:
      return "bg-pink-500/20 text-pink-300 border-pink-500/50";
    case UserRole.SALES:
      return "bg-amber-500/20 text-amber-300 border-amber-500/50";
    case UserRole.CUSTOMER_SUPPORT:
      return "bg-cyan-500/20 text-cyan-300 border-cyan-500/50";
    case UserRole.VIEWER:
      return "bg-gray-500/20 text-gray-300 border-gray-500/50";
  }
}

function getRoleRingColor(role: UserRole) {
  switch (role) {
    case UserRole.ADMIN:
      return "focus-visible:ring-red-500/45";
    case UserRole.DEVELOPER:
      return "focus-visible:ring-emerald-500/45";
    case UserRole.PRODUCT:
      return "focus-visible:ring-blue-500/45";
    case UserRole.MARKETING:
      return "focus-visible:ring-pink-500/45";
    case UserRole.SALES:
      return "focus-visible:ring-amber-500/45";
    case UserRole.CUSTOMER_SUPPORT:
      return "focus-visible:ring-cyan-500/45";
    case UserRole.VIEWER:
      return "focus-visible:ring-gray-500/45";
  }
}

interface SettingsMembersSectionProps {
  isActive: boolean;
  refreshKey: number;
}

type MembersView = "members" | "matrix";

const SettingsMembersSection: React.FC<SettingsMembersSectionProps> = ({
  isActive,
  refreshKey,
}) => {
  const user = useSelector(selectUser);
  const currentUserProfile = useSelector(selectUserProfile);

  const activityActor = actorFromAuth(user, currentUserProfile);

  const canManage = Boolean(
    currentUserProfile &&
    hasPermission(currentUserProfile.role, Permission.MANAGE_USERS),
  );

  const [view, setView] = useState<MembersView>("members");

  const [users, setUsers] = useState<UserWithUid[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    if (!canManage) return;

    try {
      setLoading(true);
      setError(null);

      const usersData = await getAllUsers();

      const usersArray: UserWithUid[] = Object.entries(usersData)
        .map(([uid, profile]) => ({
          uid,
          ...profile,
        }))
        .sort((a, b) => b.createdAt - a.createdAt);

      setUsers(usersArray);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load members";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }

    if (!isActive) return;

    void loadUsers();
  }, [canManage, isActive, refreshKey, loadUsers]);

  const handleRoleChange = async (uid: string, newRole: UserRole) => {
    try {
      setUpdatingUser(uid);
      await updateUserRole(uid, newRole, activityActor ?? undefined);
      setUsers((prev) =>
        prev.map((member) =>
          member.uid === uid ? { ...member, role: newRole } : member,
        ),
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Unknown error";
      alert(`Failed to update role: ${message}`);
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleToggleActive = async (uid: string, currentStatus: boolean) => {
    try {
      setUpdatingUser(uid);
      await updateUserActiveStatus(
        uid,
        !currentStatus,
        activityActor ?? undefined,
      );
      setUsers((prev) =>
        prev.map((member) =>
          member.uid === uid ? { ...member, isActive: !currentStatus } : member,
        ),
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Unknown error";
      alert(`Failed to update status: ${message}`);
    } finally {
      setUpdatingUser(null);
    }
  };

  if (!canManage) {
    return (
      <div className="flex h-full min-h-0 flex-col" style={nunito}>
        <SettingsHeader
          title="Team members"
          description="View and manage who has access to the dashboard."
        />
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pt-3">
          <div className="rounded-[1.25rem] border-4 border-white/10 bg-gray-950/50 p-6 text-center shadow-[0_6px_0_rgba(0,0,0,0.35)] sm:p-8">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border-4 border-[#6366F1]/30 bg-[#6366F1]/15">
              <Lock className="h-6 w-6 text-indigo-200" aria-hidden />
            </div>
            <p className="font-extrabold text-white">
              Administrator access required
            </p>
            <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">
              Only administrators can invite, change teams, or deactivate
              members. Contact your admin if you need changes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col" style={nunito}>
      <SettingsHeader
        title="Team members"
        description="Assign teams and control who can sign in to the dashboard."
      />

      <div
        role="tablist"
        aria-label="Members section"
        className="mt-3 inline-flex shrink-0 rounded-xl border-2 border-white/10 bg-white/5 p-1"
      >
        {(
          [
            { id: "members" as const, label: "Members" },
            { id: "matrix" as const, label: "Role matrix" },
          ] as const
        ).map(({ id, label }) => {
          const selected = view === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setView(id)}
              className={[
                "rounded-lg px-4 py-2 text-sm font-extrabold transition-colors",
                "focus:outline-none focus-visible:ring-4 focus-visible:ring-white/20",
                selected
                  ? "bg-white/15 text-white shadow-[0_2px_0_rgba(255,255,255,0.15)]"
                  : "text-gray-400 hover:bg-white/10 hover:text-white",
              ].join(" ")}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pt-4">
        {view === "matrix" ? (
          <RolePermissionsMatrix className="mb-0" embedded />
        ) : (
          <div className="space-y-4">
            {error && (
              <div
                className="rounded-2xl border-4 border-red-500/40 bg-red-500/15 px-4 py-3 text-sm text-red-100"
                role="alert"
              >
                {error}
              </div>
            )}

            {loading ? (
              <div
                className="flex items-center justify-center py-12"
                aria-busy="true"
              >
                <Loader2
                  className="h-8 w-8 animate-spin text-[#14B8A6]"
                  aria-hidden
                />
                <span className="sr-only">Loading members</span>
              </div>
            ) : (
              <div className="overflow-hidden rounded-[1.25rem] border-4 border-white/10 bg-gray-950/40 shadow-[0_6px_0_rgba(0,0,0,0.35)]">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px]">
                    <thead className="border-b-4 border-white/10 bg-white/5">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-gray-400 sm:px-6">
                          Member
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-gray-400 sm:px-6">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-gray-400 sm:px-6">
                          Team
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-gray-400 sm:px-6">
                          Status
                        </th>
                        <th className="hidden px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wide text-gray-400 lg:table-cell lg:px-6">
                          Last login
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {users.map((member) => (
                        <tr
                          key={member.uid}
                          className="transition-colors hover:bg-white/[0.03]"
                        >
                          <td className="px-4 py-3 sm:px-6">
                            <div className="flex items-center gap-2.5">
                              {getRoleIcon(member.role)}
                              <span className="font-semibold text-white">
                                {member.displayName || "Unknown"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400 sm:px-6">
                            <span className="block max-w-[14rem] truncate sm:max-w-none">
                              {member.email}
                            </span>
                          </td>
                          <td className="px-4 py-3 sm:px-6">
                            <div className="relative inline-block">
                              <select
                                value={member.role}
                                onChange={(e) =>
                                  void handleRoleChange(
                                    member.uid,
                                    e.target.value as UserRole,
                                  )
                                }
                                disabled={updatingUser === member.uid}
                                aria-label={`Team for ${member.email}`}
                                className={`appearance-none rounded-xl border py-1.5 pl-2.5 pr-9 text-sm font-semibold ${getRoleBadgeColor(
                                  member.role,
                                )} bg-transparent focus:outline-none focus-visible:ring-4 ${getRoleRingColor(
                                  member.role,
                                )} disabled:cursor-not-allowed disabled:opacity-50`}
                              >
                                {ALL_USER_ROLES.map((role) => (
                                  <option key={role} value={role}>
                                    {getRoleDisplayName(role)}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown
                                className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-current opacity-80"
                                aria-hidden
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3 sm:px-6">
                            <button
                              type="button"
                              onClick={() =>
                                void handleToggleActive(
                                  member.uid,
                                  member.isActive,
                                )
                              }
                              disabled={updatingUser === member.uid}
                              className={`flex items-center gap-2 rounded-xl border-2 px-3 py-1.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                                member.isActive
                                  ? "border-green-500/40 bg-green-500/15 text-green-200 hover:bg-green-500/25"
                                  : "border-white/15 bg-white/5 text-gray-400 hover:bg-white/10"
                              }`}
                            >
                              {updatingUser === member.uid ? (
                                <Loader2
                                  className="h-3.5 w-3.5 animate-spin"
                                  aria-hidden
                                />
                              ) : member.isActive ? (
                                <FaCheckCircle
                                  className="text-xs"
                                  aria-hidden
                                />
                              ) : (
                                <FaBan className="text-xs" aria-hidden />
                              )}
                              {member.isActive ? "Active" : "Inactive"}
                            </button>
                          </td>
                          <td className="hidden px-4 py-3 text-sm text-gray-500 lg:table-cell lg:px-6">
                            {new Date(member.lastLogin).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {users.length === 0 && (
                  <p className="py-10 text-center text-sm text-gray-500">
                    No members found.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsMembersSection;
